import React, { useEffect, useMemo, useState } from 'react';

import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { ArrowDown } from 'iconsax-react';
import { FileText } from 'lucide-react';
import * as XLSX from 'xlsx';

interface MarkdownFormatterProps {
  markdownText: string;
  referenceLinks?: {
    [key: string]: { url: string; n_processo: string; ementa: string };
  };
}

const ReferenceLink = ({
  num,
  data,
}: {
  num: string;
  data: {
    url: string;
    tribunal?: string;
    n_processo?: string;
    data_julgamento?: string;
    ementa?: string;
    relator?: string;
  };
}) => {
  const [hover, setHover] = useState(false);

  function capitalizeWords(str: string) {
    return str.replace(/\w\S*/g, function (word) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });
  }
  return (
    <span
      className="relative mx-1 inline-block"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <a
        href={data.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center w-6 h-6 text-xs font-semibold rounded-full bg-[#F2F4F7] text-[#344054] hover:bg-[#1D2939] hover:text-white transition-colors"
      >
        {num}
      </a>

      {hover && (
        <a href={data.url} target="_blank" rel="noopener noreferrer">
          <div className="absolute z-50 top-8 left-1/2 -translate-x-1/2 max-w-xs w-64 p-3 bg-white shadow-lg border border-solid border-gray-200 rounded-lg text-sm text-left space-y-1">
            {data.relator && (
              <p className="font-poppins text-[11px] font-medium text-gray-600 uppercase tracking-tight">
                {data.relator}
              </p>
            )}
            {data.n_processo && (
              <p className="text-[13px] font-semibold text-gray-800 leading-snug font-poppins">
                {data.n_processo}
              </p>
            )}
            {data.data_julgamento && (
              <p className="text-xs text-gray-400">{data.data_julgamento}</p>
            )}
            {data.ementa && (
              <p className="font-poppins text-[12px] text-gray-400 leading-snug line-clamp-3">
                {data.ementa.length > 60
                  ? capitalizeWords(data.ementa.slice(0, 200)) + '...'
                  : data.ementa}
              </p>
            )}
          </div>
        </a>
      )}
    </span>
  );
};

const MarkdownFormatter = ({
  markdownText,
  referenceLinks,
}: MarkdownFormatterProps) => {
  const sanitizeLatex = (formula: string) => {
    return formula
      .replace(/(?<=\d),(?=\d)/g, '.') // troca vírgula decimal por ponto
      .replace(/R\$/g, '\\text{R\\$}'); // escapa R$ corretamente
  };
  const [copiedCodeId, setCopiedCodeId] = useState<string | null>(null);

  const parseMarkdown = (text: string) => {
    const lines = text.split('\n');
    const result: JSX.Element[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      // headers
      const headingMatch = line.match(/^(#{1,6})\s+(.*)/);
      if (headingMatch) {
        const level = headingMatch[1].length;
        const content = headingMatch[2];

        result.push(
          React.createElement(
            `h${level}`,
            {
              key: `heading-${i}`,
              className:
                level === 1
                  ? 'text-2xl font-bold my-4'
                  : level === 2
                    ? 'text-xl font-semibold my-3'
                    : 'text-lg font-medium my-2',
            },
            parseInlineMarkdown(content),
          ),
        );
        i++;
        continue;
      }
      // Separador horizontal (---)
      if (line.trim() === '---') {
        result.push(
          <hr
            key={`hr-${i}`}
            style={{
              border: 'none',
              borderTop: '1px solid #ddd',
              margin: '1rem 0',
            }}
          />,
        );
        i++;
        continue;
      }

      // Fórmulas em bloco: \[...\]
      // Bloco LaTeX com \[ ... \] (multi-linha permitido)
      if (line.trim().startsWith('\\[')) {
        let formulaLines: string[] = [];
        let closed = false;

        // Remove a abertura
        formulaLines.push(line.replace('\\[', '').trim());
        i++;

        while (i < lines.length) {
          if (lines[i].trim().endsWith('\\]')) {
            formulaLines.push(lines[i].replace('\\]', '').trim());
            closed = true;
            i++;
            break;
          } else {
            formulaLines.push(lines[i]);
            i++;
          }
        }

        if (closed) {
          const formula = formulaLines.join(' ').trim();
          result.push(
            <BlockMath key={`block-bracket-${i}`}>
              {sanitizeLatex(formula)}
            </BlockMath>,
          );
          continue;
        }
      }

      // Fórmulas em bloco: $$...$$
      if (line.trim().startsWith('$$') && line.trim().endsWith('$$')) {
        const formula = line.trim().slice(2, -2).trim();
        result.push(
          <BlockMath key={`block-dollar-${i}`}>
            {sanitizeLatex(formula)}
          </BlockMath>,
        );
        i++;
        continue;
      }

      // Tabela markdown
      if (
        line.includes('|') &&
        i + 1 < lines.length &&
        lines[i + 1].match(/^\|?\s*-+/)
      ) {
        const tableLines: string[] = [];
        while (i < lines.length && lines[i].includes('|')) {
          tableLines.push(lines[i]);
          i++;
        }

        const renderedTable = renderTable(tableLines);
        if (renderedTable) result.push(renderedTable);
        continue;
      }

      // Lista bullet (com hifen "-" ou traço "–")
      const bulletMatch = line.match(/^\s*[–-]\s+(.*)/);
      if (bulletMatch) {
        const content = bulletMatch[1];
        result.push(
          <div
            key={i}
            style={{
              textAlign: 'left',
              fontSize: '16px',
              marginBottom: '1rem',
              marginTop: '1rem',
            }}
          >
            <ul
              style={{
                paddingLeft: '2rem',
                marginBottom: '0.25rem',
                listStyleType: 'disc',
              }}
            >
              <li>{parseInlineMarkdown(content)}</li>
            </ul>
          </div>,
        );
        i++;
        continue;
      }
      // // Lista bullet
      // if (line.startsWith('- ')) {
      //   const content = line.substring(2);
      //   result.push(
      //     <div key={i} style={{ textAlign: 'left', fontSize: '16px' }}>
      //       <ul>
      //         <li>{parseInlineMarkdown(content)}</li>
      //       </ul>
      //     </div>
      //   );
      //   i++;
      //   continue;
      // }

      // Detectar bloco de código com crases triplas
      // Detectar blocos de código cercados por ``` com ou sem linguagem
      if (line.trim().startsWith('```')) {
        const language = line.trim().slice(3).trim(); // e.g., "sql"
        i++;
        const codeLines: string[] = [];

        while (i < lines.length && !lines[i].trim().startsWith('```')) {
          codeLines.push(lines[i]);
          i++;
        }

        i++; // pular linha final ```

        const code = codeLines.join('\n');
        const codeKey = `code-block-${i}`;

        result.push(
          <div
            key={codeKey}
            style={{
              position: 'relative',
              marginBottom: '1.5rem',
              backgroundColor: '#f9f9f9',
              border: '1px solid #ddd',
              borderRadius: '8px',
              overflow: 'auto',
            }}
          >
            <button
              onClick={() => {
                navigator.clipboard.writeText(code);
                setCopiedCodeId(codeKey);

                setTimeout(() => {
                  setCopiedCodeId(null);
                }, 2000); // 2 segundos
              }}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                background: 'transparent',
                border: 'none',
                color: '#666',
                fontSize: '12px',
                padding: '4px 8px',
                cursor: 'pointer',
                opacity: 0.6,
                transition: 'opacity 0.2s ease',
              }}
              onMouseOver={(e) => (e.currentTarget.style.opacity = '1')}
              onMouseOut={(e) => (e.currentTarget.style.opacity = '0.6')}
            >
              {copiedCodeId === codeKey ? 'Copiado!' : 'Copiar'}
            </button>
            <pre
              style={{
                padding: '16px',
                margin: 0,
                marginTop: '2rem',
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                fontSize: '14px',
                color: '#333',
                overflowX: 'auto',
              }}
            >
              <code className={`language-${language}`}>{code}</code>
            </pre>
          </div>,
        );
        continue;
      }

      // Blockquote (citação com >) ( coloquei ele aqui pq nao sei se atrapalha os links)
      if (line.trim().startsWith('> ')) {
        const content = line.replace(/^>\s?/, ''); // remove o > e espaço
        result.push(
          <blockquote
            key={`blockquote-${i}`}
            className="border-l-4 border-gray-300 pl-4 italic text-gray-700 my-4"
          >
            {parseInlineMarkdown(content)}
          </blockquote>,
        );
        i++;
        continue;
      }

      // Texto comum
      result.push(
        <div key={i} style={{ marginBottom: '0.5rem' }}>
          <div
            style={{
              whiteSpace: 'pre-wrap',
              textAlign: 'left',
              fontSize: '16px',
              marginBottom: '1.5rem',
            }}
          >
            {parseInlineMarkdown(line)}
          </div>
        </div>,
      );
      i++;
    }

    return result;
  };
  const tableKey = useMemo(() => Math.random(), []);

  const renderTable = (lines: string[]) => {
    if (lines.length < 2) return null;

    const headers = lines[0]
      .split('|')
      .map((cell) => cell.trim())
      .filter(Boolean);
    const rows = lines.slice(2).map((line) =>
      line
        .split('|')
        .map((cell) => cell.trim())
        .filter(Boolean),
    );

    const downloadExcel = () => {
      // --- Aba principal (com "Fonte X" no lugar de [n]) ---
      const mainData = [
        headers,
        ...rows.map((row) => {
          const updatedRow = [...row];
          const fonteIndex = headers.findIndex((h) =>
            h.toLowerCase().includes('fonte'),
          );
          if (fonteIndex !== -1) {
            const fonteRaw = row[fonteIndex] || '';
            const matches = fonteRaw.match(/\[(\d+)\]/g) || [];
            const refs = matches.map((m) => m.replace(/\[|\]/g, ''));
            updatedRow[fonteIndex] = refs.map((ref) => `[${ref}]`).join(', ');
          }
          return updatedRow;
        }),
      ];

      const mainSheet = XLSX.utils.aoa_to_sheet(mainData);

      // --- Aba "Fontes" com apenas número da fonte, processo e relator ---
      const fontesData = [
        ['Ref', 'Processo', 'Link'],
        ...Object.entries(referenceLinks || {}).map(([key, value]) => [
          key,
          value.n_processo || '',
          value.url || '',
        ]),
      ];

      const fontesSheet = XLSX.utils.aoa_to_sheet(fontesData);

      // --- Cria o workbook ---
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, mainSheet, 'Tabela');
      XLSX.utils.book_append_sheet(wb, fontesSheet, 'Fontes');

      // --- Gera o arquivo Excel ---
      XLSX.writeFile(wb, 'tabela-com-fontes.xlsx');
    };

    return (
      <div key={`table-container-${tableKey}`} style={{ marginBottom: '2rem' }}>
        <div
          style={{
            overflowX: 'auto',
            borderRadius: '8px',
            border: '1px solid #e5e5e5',
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '15px',
              backgroundColor: 'white',
              color: '#1f1f1f',
            }}
          >
            <thead>
              <tr>
                {headers.map((cell, idx) => (
                  <th
                    key={idx}
                    style={{
                      textAlign: 'left',
                      padding: '12px',
                      borderBottom: '1px solid #e5e5e5',
                      backgroundColor: '#f8f8f8',
                      fontWeight: 600,
                      fontSize: '14px',
                    }}
                  >
                    {parseInlineMarkdown(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rIdx) => (
                <tr key={rIdx}>
                  {row.map((cell, cIdx) => (
                    <td
                      key={cIdx}
                      style={{
                        padding: '12px',
                        borderBottom: '1px solid #f0f0f0',
                        verticalAlign: 'top',
                      }}
                    >
                      {parseInlineMarkdown(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: '0.75rem' }}>
          <button
            onClick={downloadExcel}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 14px',
              background: '#010C28',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            <ArrowDown size={18} variant="Outline" />
            <span>Download Excel</span>
          </button>
        </div>
      </div>
    );
  };

  const parseInlineMarkdown = (text: string): (string | JSX.Element)[] => {
    const elements: (string | JSX.Element)[] = [];
    const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
    const boldRegex = /\*\*(.*?)\*\*/g;
    const italicRegex = /_(.*?)_/g;
    const latexInlineRegex = /\$(.+?)\$|\\\((.+?)\\\)/g;
    const referenceRegex = /\[(\d+)\]/g;

    let remaining = text;
    let matchIndex = 0;

    while (remaining.length > 0) {
      let matches: {
        type: 'link' | 'bold' | 'italic' | 'latex' | 'reference';
        match: RegExpExecArray;
      }[] = [];

      const linkMatch = [...remaining.matchAll(linkRegex)][0];
      const boldMatch = [...remaining.matchAll(boldRegex)][0];
      const italicMatch = [...remaining.matchAll(italicRegex)][0];
      const latexMatch = [...remaining.matchAll(latexInlineRegex)][0];
      const referenceMatch = [...remaining.matchAll(referenceRegex)][0];

      if (referenceMatch)
        matches.push({ type: 'reference', match: referenceMatch });
      if (linkMatch) matches.push({ type: 'link', match: linkMatch });
      if (boldMatch) matches.push({ type: 'bold', match: boldMatch });
      if (italicMatch) matches.push({ type: 'italic', match: italicMatch });
      if (latexMatch) matches.push({ type: 'latex', match: latexMatch });

      if (matches.length === 0) {
        elements.push(remaining);
        break;
      }

      const nextMatch = matches.reduce((prev, curr) =>
        prev.match.index < curr.match.index ? prev : curr,
      );

      const before = remaining.substring(0, nextMatch.match.index);
      if (before) elements.push(before);

      const m = nextMatch.match;
      const type = nextMatch.type;

      if (type === 'link') {
        elements.push(
          <a
            key={`${type}-${matchIndex}`}
            href={m[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 hover:bg-blue-200 rounded-full"
          >
            <FileText size={14} className="text-blue-800" />
          </a>,
        );
      } else if (type === 'bold') {
        elements.push(
          <strong style={{ fontWeight: 600 }} key={`${type}-${matchIndex}`}>
            {m[1]}
          </strong>,
        );
      } else if (type === 'italic') {
        elements.push(<em key={`${type}-${matchIndex}`}>{m[1]}</em>);
      } else if (type === 'latex') {
        const formula = m[1] ?? m[2];
        elements.push(
          <InlineMath key={`${type}-${matchIndex}`}>
            {sanitizeLatex(formula)}
          </InlineMath>,
        );
      } else if (type === 'reference') {
        const refNumber = m[1];
        // const refLink = referenceLinks?.[refNumber]?.url ?? null;

        elements.push(
          referenceLinks?.[refNumber] ? (
            <ReferenceLink
              key={`${type}-${matchIndex}`}
              num={refNumber}
              data={referenceLinks[refNumber]}
            />
          ) : (
            `[${refNumber}]`
          ),
        );
      }

      remaining = remaining.substring(m.index + m[0].length);
      matchIndex++;
    }

    return elements;
  };

  // const parsedContent = useMemo(() => {
  //   if (!markdownText?.trim()) return '';
  //   return parseMarkdown(markdownText);
  // }, [markdownText]);

  return (
    <div className="leading-relaxed">
      {markdownText ? parseMarkdown(markdownText) : ''}
    </div>
  );
};

export default MarkdownFormatter;
